import { useMemo } from 'react'

import omit from 'lodash/omit'

import { useExtensionData } from '$extensionSrc/hooks/extensionStorageListener'
import { ConcernTag, PLATFORM, STORAGE_KEY } from '$extensionSrc/utils/enums'
import * as localStorage from '$extensionSrc/utils/localStorage'

export type SidebarFilters = {
  concerns?: Partial<Record<ConcernTag, boolean>>
}

export async function clearSidebarFilters(platform: PLATFORM): Promise<void> {
  const currentSidebarFilters = await localStorage.getSingle(
    STORAGE_KEY.SIDEBAR_FILTERS,
  )

  return chrome.storage.local.set({
    [STORAGE_KEY.SIDEBAR_FILTERS]: omit(currentSidebarFilters, platform),
  })
}

export async function toggleSidebarConcern(
  platform: PLATFORM,
  concern: ConcernTag,
): Promise<void> {
  const currentSidebarFilters = await localStorage.getSingle(
    STORAGE_KEY.SIDEBAR_FILTERS,
  )

  const currentValue =
    currentSidebarFilters?.[platform]?.concerns?.[concern] || false

  const newSidebarFilters = {
    ...(currentSidebarFilters || {}),
    [platform]: {
      ...(currentSidebarFilters?.[platform] || {}),
      concerns: {
        ...(currentSidebarFilters?.[platform]?.concerns || {}),
        [concern]: !currentValue,
      },
    },
  }

  return chrome.storage.local.set({
    [STORAGE_KEY.SIDEBAR_FILTERS]: newSidebarFilters,
  })
}

export function useSidebarFilters(
  platform: PLATFORM | null | undefined,
): SidebarFilters | undefined {
  const { data, isLoaded } = useExtensionData({
    local: [STORAGE_KEY.SIDEBAR_FILTERS],
  })

  if (!platform || !isLoaded || !data) {
    // Still loading
    return undefined
  }

  return data[STORAGE_KEY.SIDEBAR_FILTERS]?.[platform] || {}
}

export function useNumberOfSelectedConcerns(
  filters: SidebarFilters,
): number | undefined {
  return useMemo(() => {
    if (filters === undefined) {
      return undefined
    }
    if (!filters.concerns) {
      return 0
    }
    return Object.values(filters.concerns).filter((v) => v).length
  }, [filters])
}
